import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken.toString() || null;
  } catch {
    return null;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Check if API_URL is configured
  if (!API_URL) {
    throw new Error('API URL is not configured. Please set VITE_API_URL environment variable.');
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    // credentials: 'omit' is default for cross-origin requests
    // Since we're using wildcard CORS (*), credentials are not needed
    // Browser automatically adds Origin header for cross-origin requests
  });
  
  // Check if response has content before parsing JSON
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');
  
  // Handle empty responses
  const text = await response.text();
  if (!text) {
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
    throw new Error('Empty response from server');
  }
  
  // Parse JSON only if content-type indicates JSON
  let data: any;
  try {
    data = isJson ? JSON.parse(text) : { error: text };
  } catch (parseError) {
    // If parsing fails, it might be an HTML error page or other non-JSON response
    console.error('Failed to parse response as JSON:', text.substring(0, 200));
    throw new Error(`Invalid response format. Expected JSON but received: ${contentType || 'unknown'}. Status: ${response.status}`);
  }
  
  if (!response.ok) {
    // Handle error responses
    const errorMessage = data.error || data.message || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  
  // Check if response has expected structure
  if (data.success === false) {
    throw new Error(data.error || 'Request failed');
  }
  
  // Return the data (handle both wrapped and unwrapped responses)
  return (data.data !== undefined ? data.data : data) as T;
}

export const api = {
  // Upload
  createUpload: (filename: string, mimeType: string, fileSize: number) =>
    request<{
      fileId: string;
      uploadUrl: string;
      expiresAt: string;
    }>('/upload', {
      method: 'POST',
      body: JSON.stringify({ filename, mimeType, fileSize }),
    }),

  uploadToS3: async (uploadUrl: string, file: File, onProgress?: (progress: number) => void) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress((e.loaded / e.total) * 100);
          }
        });
      }
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },
  
  // Files
  listFiles: () =>
    request<{ files: import('../types').FileRecord[]; total: number }>('/files'),
  
  getFile: (fileId: string) =>
    request<import('../types').FileDetails>(`/files/${fileId}`),
  
  // API Keys
  listApiKeys: () =>
    request<{ apiKeys: import('../types').ApiKey[]; total: number }>('/apikeys'),
  
  createApiKey: (name: string, expiresInDays?: number) =>
    request<import('../types').CreateApiKeyResponse>('/apikeys', {
      method: 'POST',
      body: JSON.stringify({ name, expiresInDays }),
    }),

  replaceApiKey: (keyId: string) =>
    request<import('../types').CreateApiKeyResponse>(`/apikeys/${keyId}/replace`, {
      method: 'POST',
    }),
  
  revokeApiKey: (keyId: string) =>
    request<{ revoked: boolean }>(`/apikeys/${keyId}`, {
      method: 'DELETE',
    }),
  
  // Settings
  getSettings: () =>
    request<{ theme: string; emailNotifications: boolean }>('/settings'),
  
  updateSettings: (settings: { theme?: string; emailNotifications?: boolean }) =>
    request<{ theme: string; emailNotifications: boolean }>('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};
